import { useEffect, useMemo, type ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Flex, Group, Loader, Stack, Text } from '@mantine/core';
import { UserRound } from 'lucide-react';
import { useViewModel } from '@/shared/viewmodels/useViewModel';
import { useMegaGridState } from '@/design/MegaGrid/MegaGrid';
import type { MegaGridFilterInstance } from '@/design/MegaGrid/MegaGrid';
import PageLevelFilterRow from '@/shared/surfaces/PageLevelFilterRow';
import SurfaceBreadcrumbRow from '@/shared/surfaces/SurfaceBreadcrumbRow';
import SurfaceAssistScope, { SurfaceAssistToolbar } from '@/shared/surfaces/assist/SurfaceAssistScope';
import { useAssistFilters } from '@/shared/surfaces/assist/AssistSurfaceProvider';
import { useSurface } from '@/shared/surfaces/useSurface';
import TopicNotFound from '@/shared/surfaces/TopicNotFound';
import EvidenceTable from '@/shared/surfaces/EvidenceTable';
import TopicSummaryPanel from '@/shared/surfaces/TopicSummaryPanel';
import ProvenancePanel from '@/shared/surfaces/ProvenancePanel';
import EvidenceBreakdownPanel from '@/shared/surfaces/EvidenceBreakdownPanel';
import type { StatItem } from '@/design/StatBand';
import type { AnalyticsSurface } from '@/shared/surfaces/AnalyticsSurface';
import type { TopicDefinition } from '@/shared/surfaces/TopicCatalogTypes';
import type { AnalyticsFilter } from '@/shared/analytics/AnalyticsTypes';
import { NULL_DIMENSION_BUCKET } from '@/shared/analytics/AnalyticsTypes';
import { formatCompact, formatCount, humanUser, formatUsd } from '@/shared/analytics/MetricFormatting';
import { UseCasePromptsViewModel } from './UseCasePromptsViewModel';
import { USE_CASE_PROMPT_COLUMNS } from './UseCasePromptColumns';
import PageHeaderComponent from '@/design/PageHeaderComponent';
const PROMPT_PAGE_SIZE = 25;

/** Minimal definition backing the shared side panels (confidence + provenance read the primary field from it). */
const PERSON_PROMPTS_DEFINITION: TopicDefinition = {
    id: 'use-case-person',
    title: 'Prompts',
    hook: '',
    question: '',
    icon: UserRound,
    accent: 'teal',
    vizKind: 'ranked-bars',
    primaryField: 'task_category',
    headlineMetric: 'turns',
    unitLabel: 'prompts',
    lensLabel: 'task category',
    lensPlural: 'task categories',
};

/**
 * A person's prompts, reached from a use case. Fetches ALL the person's
 * prompts and applies the originating category as a seeded, removable grid
 * filter — so the viewer can switch or widen the category in place instead of
 * navigating back out.
 */
export default function UseCasePersonPage(): ReactElement {
    const surface = useSurface('behavior');
    if (surface === undefined) return <TopicNotFound />;
    return (
        <SurfaceAssistScope surface={surface}>
            <UseCasePersonBody surface={surface} />
        </SurfaceAssistScope>
    );
}

function UseCasePersonBody({ surface }: { surface: AnalyticsSurface }): ReactElement {
    const { category, subcategory, userId } = useParams<{ category: string; subcategory?: string; userId: string }>();
    if (category === undefined || category.length === 0 || userId === undefined || userId.length === 0) return <TopicNotFound />;
    return <UseCasePersonContent key={`${category}:${subcategory ?? ''}:${userId}`} surface={surface} category={category} subcategory={subcategory ?? null} userId={userId} />;
}

function UseCasePersonContent({ surface, category, subcategory, userId }: { surface: AnalyticsSurface; category: string; subcategory: string | null; userId: string }): ReactElement {
    const ambientFilters = useAssistFilters();
    const vm = useViewModel(UseCasePromptsViewModel);

    const filters = useMemo<AnalyticsFilter[]>(() => [{ field: 'user_id', values: [userId] }, ...ambientFilters], [userId, ambientFilters]);
    const defaultFilters = useMemo<MegaGridFilterInstance[]>(
        () => [
            { id: 'seed-category', columnKey: 'category', op: 'anyOf', values: [category] },
            ...(subcategory === null ? [] : [{ id: 'seed-task', columnKey: 'task', op: 'anyOf' as const, values: [subcategory] }]),
        ],
        [category, subcategory],
    );
    const gridPersistenceKey = `use-case-person-prompts:${category}:${subcategory ?? 'all'}`;
    const controller = useMegaGridState({
        columns: USE_CASE_PROMPT_COLUMNS,
        persistenceKey: gridPersistenceKey,
        defaultDensity: 'comfortable',
        defaultGroupBy: [],
        allowMultiSort: true,
        defaultFilters,
    });

    useEffect(() => {
        void vm.load(filters);
    }, [vm, filters]);

    const basePath = `${surface.basePath}/use-cases`;
    const personLabel = humanUser(userId);
    const stats: StatItem[] = [
        { label: 'Prompts', value: formatCount(vm.summary.turns) },
        { label: 'Sessions', value: formatCount(vm.summary.sessions) },
        { label: 'Total tokens', value: formatCompact(vm.summary.totalTokens) },
        { label: 'Cost', value: formatUsd(vm.summary.allocatedCostUsd) },
    ];

    return (
        <Box pb="xl">
            <PageLevelFilterRow assist={<SurfaceAssistToolbar surface={surface} />} />

            <SurfaceBreadcrumbRow
                items={[
                    { label: 'Use Cases', to: basePath },
                    { label: category === NULL_DIMENSION_BUCKET ? 'Unclassified' : category, to: `${basePath}/${encodeURIComponent(category)}` },
                    ...(subcategory === null ? [] : [{ label: subcategory === NULL_DIMENSION_BUCKET ? 'Unclassified' : subcategory, to: `${basePath}/${encodeURIComponent(category)}/sub/${encodeURIComponent(subcategory)}` }]),
                    { label: personLabel },
                ]}
            />

            <PageHeaderComponent
                icon={UserRound}
                eyebrow="Person · prompts"
                title={personLabel}
                subtitle="Everything this person has asked of AI — each prompt classified, with its confidence."
            />

            {vm.errorMessage !== null && (
                <Text size="sm" c="red" mb="md">
                    {vm.errorMessage}
                </Text>
            )}

            {vm.loading && vm.records.length === 0 ?
                <Group justify="center" gap="sm" py="xl">
                    <Loader size="sm" />
                    <Text size="sm" c="dimmed">
                        Loading prompts…
                    </Text>
                </Group>
            :   <Flex gap="lg" align="flex-start" direction={{ base: 'column', lg: 'row' }}>
                    <Box style={{ flex: 1, minWidth: 0, width: '100%' }}>
                        <EvidenceTable records={vm.records} accent="teal" columns={USE_CASE_PROMPT_COLUMNS} clientPageSize={PROMPT_PAGE_SIZE} controller={controller} persistenceKey={gridPersistenceKey} />
                        {vm.totalMatched > vm.records.length && (
                            <Text size="xs" c="dimmed" mt="sm">
                                Showing the {formatCount(vm.records.length)} most recent of {formatCount(vm.totalMatched)} prompts by this person.
                            </Text>
                        )}
                    </Box>
                    <Box style={{ width: 300, flexShrink: 0, maxWidth: '100%' }}>
                        <Stack gap="md">
                            <TopicSummaryPanel stats={stats} />
                            <ProvenancePanel definition={PERSON_PROMPTS_DEFINITION} records={vm.records} confidenceOnly />
                            <EvidenceBreakdownPanel records={vm.records} />
                        </Stack>
                    </Box>
                </Flex>
            }
        </Box>
    );
}
